import type {
  StaffAuditPage,
  StaffNotificationFailurePage,
  StaffPaymentExceptionList,
  StaffProviderHealth,
} from "@lafayette/schemas";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useDiagnosticsRuntime } from "./runtime";

// The reads of the diagnostics pages. Nothing stays in the cache after the page lets go (`gcTime: 0`), because the answers name records, and a
// failed read is not retried by the library: the page shows the error and offers a retry. Every read runs again when a page opens, so a screen
// never shows the state from before an action. The failed jobs and the task queues come from the Operations hooks (`../operations/queries`).

export const diagnosticsKeys = {
  all: ["staff-diagnostics"] as const,
  health: ["staff-diagnostics", "health"] as const,
  notifications: ["staff-diagnostics", "notifications"] as const,
  exceptions: ["staff-diagnostics", "exceptions"] as const,
  audit: (filter: string) => ["staff-diagnostics", "audit", filter] as const,
};

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnMount: "always",
} as const;

const DIAGNOSTICS_PAGE_SIZE = 25;

export function useProviderHealth(enabled: boolean) {
  const { health } = useDiagnosticsRuntime();
  return useQuery({
    queryKey: diagnosticsKeys.health,
    queryFn: ({ signal }): Promise<StaffProviderHealth> => health.getProviderHealth({ signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

export function usePaymentExceptions(enabled: boolean) {
  const { api } = useDiagnosticsRuntime();
  return useQuery({
    queryKey: diagnosticsKeys.exceptions,
    queryFn: ({ signal }): Promise<StaffPaymentExceptionList> =>
      api.listPaymentExceptions({ signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

export function useNotificationFailures(enabled: boolean) {
  const { api } = useDiagnosticsRuntime();
  return useInfiniteQuery({
    queryKey: diagnosticsKeys.notifications,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }): Promise<StaffNotificationFailurePage> =>
      api.listNotificationFailures(
        { limit: DIAGNOSTICS_PAGE_SIZE, ...(pageParam === null ? {} : { cursor: pageParam }) },
        { signal },
      ),
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    ...READ_OPTIONS,
  });
}

/** The filter of the audit page, as the text the fields hold. An empty text is no filter. `from` and `to` are ISO instants. */
export type AuditFilter = Readonly<{
  actorId: string;
  action: string;
  targetType: string;
  targetId: string;
  from: string;
  to: string;
}>;

export const emptyAuditFilter: AuditFilter = {
  actorId: "",
  action: "",
  targetType: "",
  targetId: "",
  from: "",
  to: "",
};

export function useAuditEvents(filter: AuditFilter, enabled: boolean) {
  const { api } = useDiagnosticsRuntime();
  return useInfiniteQuery({
    queryKey: diagnosticsKeys.audit(JSON.stringify(filter)),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }): Promise<StaffAuditPage> =>
      api.listAuditEvents(
        {
          ...(filter.actorId === "" ? {} : { actorId: filter.actorId }),
          ...(filter.action === "" ? {} : { action: filter.action }),
          ...(filter.targetType === "" ? {} : { targetType: filter.targetType }),
          ...(filter.targetId === "" ? {} : { targetId: filter.targetId }),
          ...(filter.from === "" ? {} : { from: filter.from }),
          ...(filter.to === "" ? {} : { to: filter.to }),
          ...(pageParam === null ? {} : { cursor: pageParam }),
          limit: DIAGNOSTICS_PAGE_SIZE,
        },
        { signal },
      ),
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    ...READ_OPTIONS,
  });
}
